import { computed, inject, Injectable, Signal } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition
} from '@angular/material/snack-bar';
import { LogService } from '@ottobock/shared/domain';

import { useFirmwareStore } from '../../../+state/firmware-package/firmware-api.store';
import { StepperFormStore } from '../../../+state/firmware-package/stepper-form.store';
import { extractVersionFromXmlContent } from '../../../helpers/extractVersionFromXmlContent';
import { readXmlFromZip } from '../../../helpers/readXmlFromZip';
@Injectable({ providedIn: 'root' })
export class FileUploadService {
  private _snackBar = inject(MatSnackBar);
  private store = inject(StepperFormStore);
  private firmwareStore = inject(useFirmwareStore);
  private logService = inject(LogService);
  files: Signal<File[]> = this.store.selectedFiles;
  firmwareVersionStatus = computed(() => this.firmwareStore.packageVersion.data?.());

  horizontalPosition: MatSnackBarHorizontalPosition = 'start';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';

  async processUploadedFile(fileList: FileList): Promise<void> {
    const files = Array.from(fileList);
    if (!files.length) return;
    const [file] = files;

    let xmlContent: string;
    try {
      xmlContent = await readXmlFromZip(file);
    } catch {
      this.handleFileError('The uploaded file could not be processed', true);
      return;
    }

    let version: string;
    try {
      version = extractVersionFromXmlContent(xmlContent);
      this.store.setUploadedManifestVersion(version);
    } catch {
      this.handleFileError('Invalid XML structure');
      return;
    }
    try {
      await this.firmwareStore.loadPackageVersion(version);

      const firmwarePackageVersion = this.firmwareVersionStatus();
      if (!firmwarePackageVersion) {
        throw new Error('Failed to load package version');
      }

      if (firmwarePackageVersion.availabilityStatus !== 'Available') {
        this.handleFileError('', true);
        this.store.clearFiles();
        this._snackBar.open(
          'Bundle version is already in the database. Please choose another firmware file.',
          'close');
        return;
      }
    } catch (err: unknown) {
      this._snackBar.open('Processing the file failed. Please try again.', 'close');
      this.logService.error(err);
      return;
    }
    this.store.addFiles(files);
    this.store.updateFileUpload({
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      file,
      isValid: true
    });
  }

  private handleFileError(message?: string, resetFile = false) {
    this.store.updateFileUpload(
      resetFile
        ? {
            file: null,
            fileName: '',
            fileSize: 0,
            fileType: '',
            isValid: false
          }
        : { isValid: false },
      message
    );
  }
}
